import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Calendar, Clock, Users, Plus, User, Car, Download, X, MapPin, Check, Trash2, Mail, Video } from 'lucide-react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useGamification } from '../contexts/GamificationContext';
import { supabase } from '../lib/supabase';
import { exportAgendamentosPortariaToExcel } from '../utils/excelExports';
import { VeiculosTab } from '../components/VeiculosTab';

const salas = [
  { id: 'aquario', name: 'Sala Aquário', capacity: 6, color: '#3B82F6' },
  { id: 'grande', name: 'Sala Grande', capacity: 12, color: '#10B981' },
  { id: 'pequena', name: 'Sala Pequena', capacity: 6, color: '#F59E0B' },
  { id: 'diretoria', name: 'Sala Diretoria', capacity: 12, color: '#EF4444' },
];

const SALAS_ALL = { id: 'all', name: 'Todas as salas', capacity: 0, color: '#6366F1' };
const salasComTodas = [SALAS_ALL, ...salas];

const empresas = ['Cropfield', 'Solus', 'Cropfert', 'Transcrop', 'Genoma', 'Outra'];

const locaisVisita = ['Cropfield Apucarana', 'Cropfert Jandaia do Sul', 'Genoma'];

// Formata no horário local do navegador (YYYY-MM-DDTHH:mm), sem converter para UTC
// como faz toISOString(), que adiantava 3h no horário de Brasília.
const toLocalInput = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

export const ReservaSalas: React.FC = () => {
  const { user } = useAuth();
  const { addActivity } = useGamification();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'salas' | 'portaria' | 'veiculos'>(() => {
    const tab = searchParams.get('tab');
    if (tab === 'veiculos') return 'veiculos';
    if (tab === 'portaria') return 'portaria';
    return 'salas';
  });
  const checklistId = searchParams.get('checklist') || undefined;
  const [selectedRoom, setSelectedRoom] = useState<string>('all');
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reservationData, setReservationData] = useState({
    sala: '',
    motivo: '',
    observacoes: '',
    inicio: '',
    fim: '',
    participantes: [] as { email: string; nome?: string }[],
  });
  const [participanteInput, setParticipanteInput] = useState('');
  const [googleSyncEnabled, setGoogleSyncEnabled] = useState(false);

  const [portariaData, setPortariaData] = useState({
    data: '',
    hora: '',
    visitante: '',
    documento: '',
    observacao: '',
    localVisita: 'Cropfield Apucarana',
    modeloVeiculo: '',
    placaVeiculo: '',
  });

  const [events, setEvents] = useState<any[]>([]);
  const [agendamentos, setAgendamentos] = useState<any[]>([]);

  const isPortaria = user?.role === 'portaria';
  const isAdmin = user?.role === 'admin' || user?.setor === 'RH';
  const canViewAllAgendamentos = isPortaria || isAdmin;
  const canEditPortaria = isAdmin;
  const canExportPortaria = isPortaria || isAdmin;

  useEffect(() => {
    loadReservations();
    void loadGoogleSyncStatus();
  }, []);

  const loadGoogleSyncStatus = async () => {
    try {
      const { data } = await supabase
        .from('google_calendar_config')
        .select('sync_enabled')
        .eq('id', 1)
        .maybeSingle();
      setGoogleSyncEnabled(!!data?.sync_enabled);
    } catch {
      setGoogleSyncEnabled(false);
    }
  };

  const triggerGoogleSync = async (action: 'create' | 'update' | 'delete', reservaId: string) => {
    if (!googleSyncEnabled) return;
    try {
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/google-calendar-sync`;
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY;
      await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ action, reserva_id: reservaId }),
      });
    } catch (err) {
      console.warn('[GCAL] sync failed silently:', err);
    }
  };

  useEffect(() => {
    if (user) {
      loadAgendamentos();
    }
  }, [user]);

  const loadReservations = async () => {
    try {
      const { data, error } = await supabase
        .from('reservas_salas')
        .select('*, usuarios(nome)')
        .order('data_reserva', { ascending: false })
        .order('hora_inicio', { ascending: false });

      if (error) throw error;

      const formattedEvents = (data || []).map((reserva: any) => {
        const sala = salas.find(s => s.id === reserva.sala);
        const originPrefix = reserva.origin === 'google' ? '[GCal] ' : '';
        return {
          id: reserva.id,
          title: `${originPrefix}${reserva.motivo} - ${reserva.usuarios?.nome || reserva.organizador_email || 'Usuário'}`,
          start: `${reserva.data_reserva}T${reserva.hora_inicio}`,
          end: `${reserva.data_reserva}T${reserva.hora_fim}`,
          backgroundColor: sala?.color || '#3B82F6',
          borderColor: reserva.origin === 'google' ? '#0F9D58' : (sala?.color || '#3B82F6'),
          extendedProps: {
            sala: reserva.sala,
            salaName: sala?.name || reserva.sala,
            motivo: reserva.motivo,
            observacoes: reserva.observacoes,
            responsavel: reserva.usuarios?.nome || reserva.organizador_email || 'Usuário',
            status: reserva.status,
            origin: reserva.origin || 'intranet',
            participantes: reserva.participantes || [],
            meetLink: reserva.google_meet_link,
            syncStatus: reserva.sync_status,
          },
        };
      });
      setEvents(formattedEvents);
    } catch (error) {
      console.error('[RESERVAS] Error loading reservations:', error);
      toast.error('Erro ao carregar reservas');
      setEvents([]);
    }
  };

  const roomEvents = selectedRoom === 'all'
    ? events
    : events.filter(event => event.extendedProps.sala === selectedRoom);

  const handleDateClick = (selectInfo: any) => {
    const startTime = toLocalInput(selectInfo.start);
    const endDate = new Date(selectInfo.start);
    endDate.setHours(endDate.getHours() + 1);
    const endTime = toLocalInput(endDate);

    setReservationData({
      sala: selectedRoom === 'all' ? '' : selectedRoom,
      motivo: '',
      observacoes: '',
      inicio: startTime,
      fim: endTime,
      participantes: [],
    });
    setParticipanteInput('');
    setShowReservationModal(true);
  };

  const loadAgendamentos = async () => {
    try {
      let query = supabase
        .from('portaria_agendamentos')
        .select('*, usuarios(nome)')
        .order('data', { ascending: false })
        .order('hora', { ascending: false });

      if (!canViewAllAgendamentos && user?.dbId) {
        query = query.eq('usuario_id', user.dbId);
      }

      const { data, error } = await query;

      if (error) throw error;

      setAgendamentos(data || []);
    } catch (error) {
      console.error('[PORTARIA] Error loading appointments:', error);
      setAgendamentos([]);
    }
  };


  const handleReservation = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (!reservationData.sala || !reservationData.inicio || !reservationData.fim || !reservationData.motivo) {
      toast.error('Preencha todos os campos obrigatórios!');
      setLoading(false);
      return;
    }

    if (!user?.dbId) {
      console.error('[RESERVA] User dbId not found:', user);
      toast.error('Erro: usuário não identificado. Faça login novamente.');
      setLoading(false);
      return;
    }

    try {
      const dataReserva = reservationData.inicio.split('T')[0];
      const horaInicio = reservationData.inicio.split('T')[1] + ':00';
      const horaFim = reservationData.fim.split('T')[1] + ':00';

      const { data: conflictingReservations, error: conflictError } = await supabase
        .from('reservas_salas')
        .select('id, hora_inicio, hora_fim, usuarios(nome)')
        .eq('sala', reservationData.sala)
        .eq('data_reserva', dataReserva)
        .neq('status', 'cancelled');

      if (conflictError) throw conflictError;

      const hasConflict = conflictingReservations?.some((reserva: any) => {
        const existingStart = reserva.hora_inicio;
        const existingEnd = reserva.hora_fim;

        return (
          (horaInicio >= existingStart && horaInicio < existingEnd) ||
          (horaFim > existingStart && horaFim <= existingEnd) ||
          (horaInicio <= existingStart && horaFim >= existingEnd)
        );
      });

      if (hasConflict) {
        const conflictDetails = conflictingReservations?.find((reserva: any) => {
          const existingStart = reserva.hora_inicio;
          const existingEnd = reserva.hora_fim;
          return (
            (horaInicio >= existingStart && horaInicio < existingEnd) ||
            (horaFim > existingStart && horaFim <= existingEnd) ||
            (horaInicio <= existingStart && horaFim >= existingEnd)
          );
        });

        const salaInfo = salas.find(s => s.id === reservationData.sala);
        toast.error(
          `${salaInfo?.name} já está reservada neste horário (${conflictDetails?.hora_inicio?.slice(0, 5)} - ${conflictDetails?.hora_fim?.slice(0, 5)}) por ${conflictDetails?.usuarios?.nome || 'outro usuário'}`
        );
        setLoading(false);
        return;
      }

      const { data: inserted, error } = await supabase
        .from('reservas_salas')
        .insert({
          usuario_id: user?.dbId,
          sala: reservationData.sala,
          data_reserva: dataReserva,
          hora_inicio: horaInicio,
          hora_fim: horaFim,
          motivo: reservationData.motivo,
          observacoes: reservationData.observacoes || null,
          status: 'pending',
          origin: 'intranet',
          participantes: reservationData.participantes,
          organizador_email: user?.email || null,
          sync_status: googleSyncEnabled ? 'pending' : 'disabled',
        })
        .select('id')
        .maybeSingle();

      if (error) throw error;

      if (inserted?.id) {
        void triggerGoogleSync('create', inserted.id);
      }

      const salaInfo = salas.find(s => s.id === reservationData.sala);

      addActivity('room_reservation', `Reservou ${salaInfo?.name}`, {
        sala: reservationData.sala,
        motivo: reservationData.motivo
      });

      toast.success(
        googleSyncEnabled
          ? `Reserva de ${salaInfo?.name} realizada! Convites do Google enviados. +8 pontos`
          : `Reserva de ${salaInfo?.name} realizada com sucesso! +8 pontos`
      );
      setShowReservationModal(false);
      setReservationData({ sala: '', motivo: '', observacoes: '', inicio: '', fim: '', participantes: [] });
      setParticipanteInput('');
      setSelectedRoom(reservationData.sala);
      await loadReservations();
    } catch (error: any) {
      console.error('[RESERVAS] Error creating reservation:', error);
      toast.error('Erro ao criar reserva: ' + (error.message || 'Tente novamente'));
    } finally {
      setLoading(false);
    }
  };

  const handlePortariaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (!portariaData.data || !portariaData.hora || !portariaData.visitante) {
      toast.error('Preencha todos os campos obrigatórios!');
      setLoading(false);
      return;
    }

    if (!user?.dbId) {
      console.error('[PORTARIA] User dbId not found:', user);
      toast.error('Erro: usuário não identificado. Faça login novamente.');
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase
        .from('portaria_agendamentos')
        .insert({
          usuario_id: user?.dbId,
          data: portariaData.data,
          hora: portariaData.hora + ':00',
          visitante: portariaData.visitante,
          documento: portariaData.documento || null,
          observacao: portariaData.observacao || null,
          local_visita: portariaData.localVisita,
          modelo_veiculo: portariaData.modeloVeiculo || null,
          placa_veiculo: portariaData.placaVeiculo || null,
          status: 'pending',
        });

      if (error) throw error;

      addActivity('reception_appointment', `Agendou visita de ${portariaData.visitante}`, {
        visitante: portariaData.visitante,
        data: portariaData.data
      });

      toast.success('Agendamento realizado com sucesso! +6 pontos');
      setPortariaData({
        data: '',
        hora: '',
        visitante: '',
        documento: '',
        observacao: '',
        localVisita: 'Cropfield Apucarana',
        modeloVeiculo: '',
        placaVeiculo: ''
      });
      await loadAgendamentos();
    } catch (error: any) {
      console.error('[PORTARIA] Error creating appointment:', error);
      toast.error('Erro ao criar agendamento: ' + (error.message || 'Tente novamente'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReserva = async (reservaId: string, tipo: 'sala' | 'portaria') => {
    if (!confirm('Tem certeza que deseja excluir esta reserva?')) return;

    try {
      const tabela = tipo === 'sala' ? 'reservas_salas' : 'portaria_agendamentos';

      if (tipo === 'sala') {
        await triggerGoogleSync('delete', reservaId);
      }

      const { error } = await supabase
        .from(tabela)
        .delete()
        .eq('id', reservaId);

      if (error) throw error;

      toast.success('Reserva excluída com sucesso!');

      if (tipo === 'sala') await loadReservations();
      else await loadAgendamentos();
    } catch (error: any) {
      console.error(`[${tipo.toUpperCase()}] Error deleting reservation:`, error);
      toast.error('Erro ao excluir reserva: ' + (error.message || 'Tente novamente'));
    }
  };


  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-headline-lg text-on-surface mb-1">Reserva de Espacos</h1>
            <p className="text-body-md text-secondary">Gerencie suas reservas de salas, veiculos e portaria de forma agil.</p>
          </div>
          <div className="flex items-center gap-3">
            {activeTab === 'portaria' && canExportPortaria && (
              <button
                onClick={exportAgendamentosPortariaToExcel}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-bold text-sm"
              >
                <Download className="w-4 h-4" />
                Exportar Excel
              </button>
            )}
            {activeTab === 'salas' && (
              <button
                onClick={() => {
                  setReservationData({
                    sala: selectedRoom === 'all' ? '' : selectedRoom,
                    motivo: '',
                    observacoes: '',
                    inicio: '',
                    fim: '',
                    participantes: [],
                  });
                  setParticipanteInput('');
                  setShowReservationModal(true);
                }}
                className="bg-primary text-white px-6 py-2.5 rounded-lg hover:bg-primary-dark transition-colors flex items-center gap-2 font-bold shadow-sm active:scale-95 duration-150"
              >
                <Plus className="w-4 h-4" />
                <span>Nova Reserva</span>
              </button>
            )}
          </div>
        </div>

        <div className="flex gap-2 p-1.5 bg-surface-container rounded-xl w-fit">
          <button
            onClick={() => setActiveTab('salas')}
            className={`px-6 py-2 rounded-lg font-bold text-sm transition-all ${
              activeTab === 'salas' ? 'bg-white dark:bg-gray-800 text-primary shadow-sm' : 'text-secondary hover:bg-white/50 dark:hover:bg-gray-700/50'
            }`}
          >
            Salas de Reuniao
          </button>
          <button
            onClick={() => setActiveTab('portaria')}
            className={`px-6 py-2 rounded-lg font-bold text-sm transition-all ${
              activeTab === 'portaria' ? 'bg-white dark:bg-gray-800 text-primary shadow-sm' : 'text-secondary hover:bg-white/50 dark:hover:bg-gray-700/50'
            }`}
          >
            Portaria
          </button>
          <button
            onClick={() => setActiveTab('veiculos')}
            className={`px-6 py-2 rounded-lg font-bold text-sm transition-all ${
              activeTab === 'veiculos' ? 'bg-white dark:bg-gray-800 text-primary shadow-sm' : 'text-secondary hover:bg-white/50 dark:hover:bg-gray-700/50'
            }`}
          >
            Veiculos
          </button>
        </div>

        {activeTab === 'salas' && (
          <>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-outline-variant dark:border-gray-700">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-bold text-on-surface mb-1">Selecione o Espaco</h2>
                  <p className="text-sm text-secondary">Escolha a sala ideal para sua reuniao</p>
                </div>
                <div className="bg-surface-container-low rounded-lg px-4 py-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium text-on-surface">{roomEvents.length} reservas ativas</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4">
                {salasComTodas.map((sala) => (
                  <button
                    key={sala.id}
                    onClick={() => setSelectedRoom(sala.id)}
                    className={`group relative p-3 sm:p-5 rounded-xl border-2 transition-all duration-200 transform hover:scale-[1.02] ${
                      selectedRoom === sala.id
                        ? 'border-primary bg-primary/5 shadow-md'
                        : 'border-outline-variant dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-primary/40 hover:shadow-sm'
                    }`}
                  >
                    <div className="flex flex-col space-y-2 sm:space-y-3">
                      <div className="flex items-center justify-between">
                        <div
                          className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: sala.color + '20' }}
                        >
                          <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full" style={{ backgroundColor: sala.color }}></div>
                        </div>
                        {selectedRoom === sala.id && (
                          <div className="w-5 h-5 sm:w-6 sm:h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                            <Check className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                          </div>
                        )}
                      </div>
                      <div className="text-left">
                        <h3 className="font-bold text-on-surface mb-1 text-sm sm:text-base leading-tight">{sala.name}</h3>
                        {sala.id !== 'all' && (
                          <div className="flex items-center space-x-1">
                            <Users className="w-3 h-3 sm:w-4 sm:h-4 text-secondary flex-shrink-0" />
                            <span className="text-xs sm:text-sm font-medium text-secondary">{sala.capacity} pessoas</span>
                          </div>
                        )}
                        {sala.id === 'all' && (
                          <span className="text-xs sm:text-sm text-secondary">Ver todas</span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-outline-variant dark:border-gray-700 overflow-hidden">
              <div className="bg-primary px-4 sm:px-6 py-4 sm:py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 sm:space-x-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center bg-white flex-shrink-0">
                      <Calendar
                        className="w-5 h-5 sm:w-6 sm:h-6"
                        style={{ color: selectedRoom === 'all' ? SALAS_ALL.color : salas.find(s => s.id === selectedRoom)?.color }}
                      />
                    </div>
                    <div>
                      <h2 className="text-base sm:text-xl font-bold text-white leading-tight">
                        {selectedRoom === 'all' ? 'Todas as Salas' : salas.find(s => s.id === selectedRoom)?.name}
                      </h2>
                      <p className="text-primary-fixed text-xs sm:text-sm">
                        {roomEvents.length} {roomEvents.length === 1 ? 'reserva' : 'reservas'}
                      </p>
                    </div>
                  </div>
                  <div className="hidden md:flex items-center space-x-3">
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2">
                      <div className="flex items-center space-x-2 text-white">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm font-medium">08:00 - 18:00</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-3 sm:p-6">
                <FullCalendar
                  plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                  headerToolbar={{
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,timeGridWeek,timeGridDay'
                  }}
                  initialView={window.innerWidth < 768 ? 'timeGridDay' : 'timeGridWeek'}
                  editable={false}
                  selectable
                  selectMirror
                  dayMaxEvents
                  weekends
                  events={roomEvents}
                  select={handleDateClick}
                  locale="pt-br"
                  height="auto"
                  contentHeight="auto"
                  aspectRatio={window.innerWidth < 768 ? 0.8 : 1.5}
                  slotMinTime="07:00:00"
                  slotMaxTime="19:00:00"
                  slotDuration="00:30:00"
                  businessHours={{
                    daysOfWeek: [1, 2, 3, 4, 5],
                    startTime: '08:00',
                    endTime: '18:00'
                  }}
                  nowIndicator={true}
                  handleWindowResize={true}
                  windowResizeDelay={200}
                  eventClick={(info) => {
                    const props = info.event.extendedProps;
                    const start = new Date(info.event.start!);
                    const end = new Date(info.event.end!);
                    const formatTime = (date: Date) =>
                      date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

                    toast(
                      <div className="space-y-2">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="font-bold text-lg text-on-surface">{props.motivo}</div>
                            {props.origin === 'google' && (
                              <span className="inline-block mt-1 px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-bold rounded-full">
                                Google Calendar
                              </span>
                            )}
                          </div>
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: info.event.backgroundColor }}
                          ></div>
                        </div>
                        {props.meetLink && (
                          <a
                            href={props.meetLink}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-2 text-sm text-primary hover:underline"
                          >
                            <Video className="w-4 h-4" /> Entrar na chamada
                          </a>
                        )}
                        {props.participantes && props.participantes.length > 0 && (
                          <div className="text-xs text-secondary">
                            {props.participantes.length} participante(s): {props.participantes.slice(0, 3).map((p: any) => p.email).join(', ')}
                            {props.participantes.length > 3 && '...'}
                          </div>
                        )}
                        <div className="flex items-center space-x-2 text-sm text-on-surface">
                          <MapPin className="w-4 h-4 text-primary" />
                          <span className="font-medium">{props.salaName}</span>
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-secondary">
                          <Clock className="w-4 h-4 text-primary" />
                          <span>{formatTime(start)} - {formatTime(end)}</span>
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-secondary">
                          <User className="w-4 h-4 text-secondary" />
                          <span>{props.responsavel}</span>
                        </div>
                        {props.observacoes && (
                          <div className="mt-2 pt-2 border-t border-outline-variant">
                            <p className="text-xs text-secondary">{props.observacoes}</p>
                          </div>
                        )}
                        {isAdmin && (
                          <div className="mt-3 pt-3 border-t border-outline-variant">
                            <button
                              onClick={() => handleDeleteReserva(info.event.id, 'sala')}
                              className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-error/10 text-error rounded-lg hover:bg-error/20 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span className="text-sm font-medium">Excluir Reserva</span>
                            </button>
                          </div>
                        )}
                      </div>,
                      {
                        duration: 5000,
                        style: {
                          minWidth: '300px',
                        }
                      }
                    );
                  }}
                  buttonText={{
                    today: 'Hoje',
                    month: 'Mês',
                    week: 'Semana',
                    day: 'Dia'
                  }}
                />
              </div>
            </div>
          </>
        )}

        {activeTab === 'portaria' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-outline-variant dark:border-gray-700">
            <h2 className="text-lg font-bold text-on-surface mb-6">Agendamentos da Portaria</h2>

            <form onSubmit={handlePortariaSubmit} className="mb-8 p-5 border border-outline-variant rounded-xl bg-surface-container-lowest">
              <h3 className="text-base font-bold text-on-surface mb-4">Novo Agendamento</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-on-surface mb-2">Data *</label>
                  <input
                    type="date"
                    value={portariaData.data}
                    onChange={(e) => setPortariaData({ ...portariaData, data: e.target.value })}
                    className="w-full px-3 py-2.5 border border-outline-variant rounded-lg bg-surface-container-lowest text-on-surface focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-on-surface mb-2">Hora *</label>
                  <input
                    type="time"
                    value={portariaData.hora}
                    onChange={(e) => setPortariaData({ ...portariaData, hora: e.target.value })}
                    className="w-full px-3 py-2.5 border border-outline-variant rounded-lg bg-surface-container-lowest text-on-surface focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-on-surface mb-2">Nome do Visitante *</label>
                  <input
                    type="text"
                    value={portariaData.visitante}
                    onChange={(e) => setPortariaData({ ...portariaData, visitante: e.target.value })}
                    className="w-full px-3 py-2.5 border border-outline-variant rounded-lg bg-surface-container-lowest text-on-surface focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    placeholder="Nome completo do visitante"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-on-surface mb-2">Documento</label>
                  <input
                    type="text"
                    value={portariaData.documento}
                    onChange={(e) => setPortariaData({ ...portariaData, documento: e.target.value })}
                    className="w-full px-3 py-2.5 border border-outline-variant rounded-lg bg-surface-container-lowest text-on-surface focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    placeholder="RG, CPF ou outro documento"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-on-surface mb-2">Local da Visita *</label>
                  <select
                    value={portariaData.localVisita}
                    onChange={(e) => setPortariaData({ ...portariaData, localVisita: e.target.value })}
                    className="w-full px-3 py-2.5 border border-outline-variant rounded-lg bg-surface-container-lowest text-on-surface focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    required
                  >
                    {locaisVisita.map((local) => (
                      <option key={local} value={local}>{local}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-on-surface mb-2">Modelo do Veiculo</label>
                  <input
                    type="text"
                    value={portariaData.modeloVeiculo}
                    onChange={(e) => setPortariaData({ ...portariaData, modeloVeiculo: e.target.value })}
                    className="w-full px-3 py-2.5 border border-outline-variant rounded-lg bg-surface-container-lowest text-on-surface focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    placeholder="Ex: Fiat Toro, Honda Civic..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-on-surface mb-2">Placa do Veiculo</label>
                  <input
                    type="text"
                    value={portariaData.placaVeiculo}
                    onChange={(e) => setPortariaData({ ...portariaData, placaVeiculo: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2.5 border border-outline-variant rounded-lg bg-surface-container-lowest text-on-surface focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    placeholder="ABC-1234"
                    maxLength={8}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-on-surface mb-2">Observacoes</label>
                  <textarea
                    value={portariaData.observacao}
                    onChange={(e) => setPortariaData({ ...portariaData, observacao: e.target.value })}
                    className="w-full px-3 py-2.5 border border-outline-variant rounded-lg bg-surface-container-lowest text-on-surface focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    rows={3}
                    placeholder="Informacoes adicionais sobre a visita..."
                  />
                </div>
              </div>
              <div className="mt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-primary text-white px-6 py-2.5 rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 font-bold"
                >
                  {loading ? 'Agendando...' : 'Agendar Visita'}
                </button>
              </div>
            </form>

            <div className="space-y-4">
              <h3 className="text-base font-bold text-on-surface">Agendamentos Recentes</h3>
              {agendamentos.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-secondary/40 mx-auto mb-4" />
                  <p className="text-secondary">Nenhum agendamento encontrado</p>
                </div>
              ) : (
                [...agendamentos].sort((a, b) => {
                  const hoje = toLocalInput(new Date()).slice(0, 10);
                  const aHoje = a.data === hoje ? 0 : 1;
                  const bHoje = b.data === hoje ? 0 : 1;
                  if (aHoje !== bHoje) return aHoje - bHoje;
                  return (b.data + b.hora).localeCompare(a.data + a.hora);
                }).map((agendamento) => {
                  const hoje = toLocalInput(new Date()).slice(0, 10);
                  const isHoje = agendamento.data === hoje;
                  return (
                  <div key={agendamento.id} className={`flex items-center justify-between p-4 rounded-xl transition-colors ${
                    isHoje
                      ? 'border-2 border-primary bg-primary/5 dark:bg-primary/10 ring-2 ring-primary/30 shadow-md shadow-primary/10'
                      : 'border border-outline-variant hover:border-primary/30'
                  }`}>
                    <div className="flex items-center space-x-4">
                      <div className="bg-primary/10 rounded-lg p-3">
                        <User className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-bold text-on-surface">{agendamento.visitante}</h3>
                        <p className="text-sm text-secondary">
                          Agendado por: {agendamento.usuarios?.nome || 'Usuario'}
                        </p>
                        {agendamento.documento && (
                          <p className="text-sm text-secondary">Doc: {agendamento.documento}</p>
                        )}
                        <div className="flex items-center space-x-4 mt-1">
                          <div className="flex items-center space-x-1">
                            <Calendar className="w-4 h-4 text-secondary" />
                            <span className="text-sm text-secondary">{agendamento.data}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Clock className="w-4 h-4 text-secondary" />
                            <span className="text-sm text-secondary">{agendamento.hora}</span>
                          </div>
                          {agendamento.local_visita && (
                            <div className="flex items-center space-x-1">
                              <MapPin className="w-4 h-4 text-secondary" />
                              <span className="text-sm text-secondary">{agendamento.local_visita}</span>
                            </div>
                          )}
                        </div>
                        {(agendamento.modelo_veiculo || agendamento.placa_veiculo) && (
                          <div className="flex items-center space-x-2 mt-1">
                            <Car className="w-4 h-4 text-secondary" />
                            <span className="text-sm text-secondary">
                              {agendamento.modelo_veiculo && agendamento.placa_veiculo
                                ? `${agendamento.modelo_veiculo} - ${agendamento.placa_veiculo}`
                                : agendamento.modelo_veiculo || agendamento.placa_veiculo}
                            </span>
                          </div>
                        )}
                        {agendamento.observacao && (
                          <p className="text-xs text-secondary mt-1">{agendamento.observacao}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      {isHoje && (
                        <span className="px-2.5 py-1 text-xs font-bold bg-primary text-white rounded-full">
                          HOJE
                        </span>
                      )}
                      <span className="px-3 py-1 text-xs font-bold bg-primary/10 text-primary rounded-full">
                        {agendamento.status === 'pending' ? 'Agendado' : agendamento.status}
                      </span>
                      {canEditPortaria && (
                        <button
                          onClick={() => handleDeleteReserva(agendamento.id, 'portaria')}
                          className="text-error hover:bg-error/10 p-2 rounded-lg transition-colors"
                          title="Excluir agendamento"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {activeTab === 'veiculos' && <VeiculosTab checklistId={checklistId} />}

        {showReservationModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-2 sm:p-4 backdrop-blur-sm overflow-y-auto">
            <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-lg my-4 sm:my-8 transform transition-all max-h-[95vh] overflow-y-auto">
              <div className="bg-primary px-4 sm:px-6 py-4 sm:py-5 rounded-t-2xl sticky top-0 z-10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                    </div>
                    <h2 className="text-base sm:text-xl font-bold text-white">Nova Reserva de Sala</h2>
                  </div>
                  <button
                    onClick={() => setShowReservationModal(false)}
                    className="text-white hover:bg-white/20 rounded-lg p-1.5 sm:p-2 transition-colors flex-shrink-0"
                  >
                    <X className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              </div>
              <div className="p-4 sm:p-6">
                <form onSubmit={handleReservation} className="space-y-3 sm:space-y-4">
                  <div>
                    <label className="block text-sm font-bold text-on-surface mb-2 sm:mb-3">Sala *</label>
                    <div className="grid grid-cols-2 gap-2 sm:gap-3">
                      {salas.map((sala) => (
                        <button
                          key={sala.id}
                          type="button"
                          onClick={() => setReservationData({ ...reservationData, sala: sala.id })}
                          className={`p-3 sm:p-4 rounded-xl border-2 transition-all text-left ${
                            reservationData.sala === sala.id
                              ? 'border-primary bg-primary/5'
                              : 'border-outline-variant dark:border-gray-700 hover:border-primary/40 bg-white dark:bg-gray-800'
                          }`}
                        >
                          <div className="flex items-center space-x-1.5 sm:space-x-2 mb-1.5 sm:mb-2">
                            <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full flex-shrink-0" style={{ backgroundColor: sala.color }}></div>
                            <span className="font-bold text-on-surface text-xs sm:text-sm leading-tight">{sala.name}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Users className="w-3 h-3 text-secondary flex-shrink-0" />
                            <span className="text-xs text-secondary">{sala.capacity} pessoas</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3 sm:space-y-4">
                    <div>
                      <label className="block text-sm font-bold text-on-surface mb-2 flex items-center space-x-2">
                        <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
                        <span>Inicio *</span>
                      </label>
                      <input
                        type="datetime-local"
                        value={reservationData.inicio}
                        onChange={(e) => setReservationData({ ...reservationData, inicio: e.target.value })}
                        className="w-full px-3 py-2.5 sm:px-4 sm:py-3 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm sm:text-base"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-on-surface mb-2 flex items-center space-x-2">
                        <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
                        <span>Fim *</span>
                      </label>
                      <input
                        type="datetime-local"
                        value={reservationData.fim}
                        onChange={(e) => setReservationData({ ...reservationData, fim: e.target.value })}
                        className="w-full px-3 py-2.5 sm:px-4 sm:py-3 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm sm:text-base"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-on-surface mb-2">Motivo *</label>
                    <input
                      type="text"
                      value={reservationData.motivo}
                      onChange={(e) => setReservationData({ ...reservationData, motivo: e.target.value })}
                      className="w-full px-3 py-2.5 sm:px-4 sm:py-3 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm sm:text-base"
                      placeholder="Ex: Reuniao de vendas, Treinamento"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-on-surface mb-2 flex items-center gap-2">
                      <Mail className="w-4 h-4 text-primary" />
                      Participantes {googleSyncEnabled && <span className="text-xs font-normal text-secondary">(convite Google Calendar sera enviado)</span>}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="email"
                        value={participanteInput}
                        onChange={(e) => setParticipanteInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
                            e.preventDefault();
                            const email = participanteInput.trim().replace(/[,;]/g, '');
                            if (email && email.includes('@') && !reservationData.participantes.some(p => p.email === email)) {
                              setReservationData({ ...reservationData, participantes: [...reservationData.participantes, { email }] });
                              setParticipanteInput('');
                            }
                          }
                        }}
                        className="flex-1 px-3 py-2.5 border border-outline-variant rounded-lg text-sm"
                        placeholder="email@empresa.com (Enter para adicionar)"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const email = participanteInput.trim();
                          if (email && email.includes('@') && !reservationData.participantes.some(p => p.email === email)) {
                            setReservationData({ ...reservationData, participantes: [...reservationData.participantes, { email }] });
                            setParticipanteInput('');
                          }
                        }}
                        className="px-3 py-2.5 bg-primary/10 text-primary rounded-lg text-sm font-bold"
                      >
                        Adicionar
                      </button>
                    </div>
                    {reservationData.participantes.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {reservationData.participantes.map((p, i) => (
                          <span key={p.email} className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-full text-xs">
                            {p.email}
                            <button
                              type="button"
                              onClick={() => setReservationData({
                                ...reservationData,
                                participantes: reservationData.participantes.filter((_, idx) => idx !== i),
                              })}
                              className="hover:bg-primary/20 rounded-full w-4 h-4 inline-flex items-center justify-center"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-on-surface mb-2">Observacoes</label>
                    <textarea
                      value={reservationData.observacoes}
                      onChange={(e) => setReservationData({ ...reservationData, observacoes: e.target.value })}
                      className="w-full px-3 py-2.5 sm:px-4 sm:py-3 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm sm:text-base"
                      rows={3}
                      placeholder="Equipamentos necessarios, etc..."
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 pt-4 sm:pt-6 border-t border-outline-variant">
                    <button
                      type="button"
                      onClick={() => setShowReservationModal(false)}
                      disabled={loading}
                      className="w-full sm:flex-1 px-4 py-2.5 sm:px-6 sm:py-3 border-2 border-outline-variant text-on-surface rounded-lg hover:bg-surface-container-low font-medium transition-all disabled:opacity-50 text-sm sm:text-base"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={loading || !reservationData.sala}
                      className="w-full sm:flex-1 px-4 py-2.5 sm:px-6 sm:py-3 bg-primary text-white rounded-lg hover:bg-primary-dark font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                    >
                      {loading ? 'Reservando...' : 'Confirmar Reserva'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};